import { useContext, useEffect, useState } from 'react';
import { Box, Button, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { CalcQuestionSelect } from '../../presentation-components/surveys/builder/calc-question-select';
import { ConditionBuilder } from '../../presentation-components/surveys/builder/condition-builder';
import { ConversionFactorPicker, ConversionSelection } from '../../presentation-components/surveys/builder/conversion-factor-picker';
import { useConversionYearFromReportingSelection } from '../../presentation-components/surveys/builder/use-conversion-year';
import { sanitizeConditions, choiceOptionsLookupFor } from '../../../shared/utils/survey-rules/condition-utils';
import { SINGLE_QUESTION_ALIAS } from '../../../shared/utils/survey-rules/formula';
import { defaultPathForQuestion, firstNumericItem, pathLabel } from '../../../shared/utils/survey-rules/question-eligibility';
import {
  ICreateEmissionRule,
  ISurveyQuestionGroupEmissionRule,
  ISurveyQuestionGroupItem,
  IVisibilityCondition,
} from '../../../shared/services/survey/survey.model';

export interface RuleSaveApi {
  createEmissionRule: (groupId: string, payload: ICreateEmissionRule) => Promise<unknown>;
  updateEmissionRule: (groupId: string, ruleId: string, payload: ICreateEmissionRule) => Promise<unknown>;
}

/** What the simple dialog hands over when the user switches to the advanced formula dialog. */
export interface SimpleRuleDraft {
  name: string;
  questionId: string;
  path: string;
  alias: string;
  conversionId?: string;
  useConditions: boolean;
  conditions: IVisibilityCondition[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  groupId: string;
  groupItems: ISurveyQuestionGroupItem[];
  existingRule?: ISurveyQuestionGroupEmissionRule;
  api?: RuleSaveApi;
  /** Offered when the rule needs more than one answer or a formula; seeds the advanced dialog. */
  onSwitchToAdvanced?: (draft: SimpleRuleDraft) => void;
}

/**
 * The default way to create a rule: one answer, optionally multiplied by an emission
 * factor, optionally gated by conditions. Saves the same payload the old single-value
 * and quick-rule modes produced.
 */
export const SimpleRuleDialog = ({ open, onClose, successHandler, groupId, groupItems, existingRule, api, onSwitchToAdvanced }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const saveApi = api ?? {
    createEmissionRule: (gid: string, payload: ICreateEmissionRule) => surveyAdminService.createEmissionRule(gid, payload),
    updateEmissionRule: (gid: string, rid: string, payload: ICreateEmissionRule) => surveyAdminService.updateEmissionRule(gid, rid, payload),
  };
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportingYear = useConversionYearFromReportingSelection();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [questionId, setQuestionId] = useState('');
  const [path, setPath] = useState('numericValue');
  const [alias, setAlias] = useState(SINGLE_QUESTION_ALIAS);
  const [selection, setSelection] = useState<ConversionSelection | null>(null);
  const [useConditions, setUseConditions] = useState(false);
  const [conditions, setConditions] = useState<IVisibilityCondition[]>([]);

  const isEditMode = !!existingRule;

  useEffect(() => {
    if (!open) return;
    if (existingRule) {
      setName(existingRule.name);
      const input = existingRule.inputs?.[0];
      setQuestionId(input?.questionId ?? '');
      setPath(input?.path ?? 'numericValue');
      setAlias(input?.alias?.trim() || SINGLE_QUESTION_ALIAS);
      const aw = existingRule.scopeMetadata?.applyWhen?.allOf;
      setUseConditions(!!aw && aw.length > 0);
      setConditions(aw && aw.length ? aw.map(c => ({ ...c })) : []);
    } else {
      setName('');
      const first = firstNumericItem(groupItems);
      setQuestionId(first?.surveyQuestionId ?? '');
      setPath(defaultPathForQuestion(first));
      setAlias(SINGLE_QUESTION_ALIAS);
      setUseConditions(false);
      setConditions([]);
    }
    setSelection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingRule]);

  const calendarYear = selection?.conversion?.calendarYear
    ? String(selection.conversion.calendarYear)
    : reportingYear;

  const selectedItem = groupItems.find(i => i.surveyQuestionId === questionId);
  const usesNonDefaultPath = Boolean(questionId) && path !== defaultPathForQuestion(selectedItem);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      if (!questionId) throw new Error('Choose the question whose answer provides the amount.');
      const aliasNorm = alias.trim() || SINGLE_QUESTION_ALIAS;
      const payload: ICreateEmissionRule = {
        name: name.trim(),
        inputs: [{ alias: aliasNorm, questionId, path: path || 'numericValue' }],
        formula: { op: 'alias', value: aliasNorm },
        scopeMetadata: useConditions && conditions.length > 0
          ? { applyWhen: { allOf: sanitizeConditions(conditions, choiceOptionsLookupFor(groupItems)) } }
          : null,
        conversionId: selection?.conversionId || undefined,
        measurementUnitId: selection?.measurementUnitId || undefined,
        groupId: selection?.conversionId ? (selection.groupId || null) : null,
      };
      if (isEditMode && existingRule) {
        await saveApi.updateEmissionRule(groupId, existingRule.id, payload);
      } else {
        await saveApi.createEmissionRule(groupId, payload);
      }
      userNotificationsContext.openSnackbar({
        open: true,
        message: isEditMode ? 'Rule updated' : 'Rule created',
        backgroundColor: 'primary.dark',
      });
      successHandler();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'An error occurred, please try again';
      userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchToAdvanced = () => {
    onSwitchToAdvanced?.({
      name,
      questionId,
      path,
      alias: alias.trim() || SINGLE_QUESTION_ALIAS,
      conversionId: selection?.conversionId,
      useConditions,
      conditions,
    });
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Simple Rule' : 'Create Simple Rule'}
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
      maxWidth='md'
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          label='Rule name'
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
        />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Which answer provides the amount?</Typography>
          <CalcQuestionSelect
            value={questionId}
            groupItems={groupItems}
            onChange={(qid, item) => {
              setQuestionId(qid);
              setPath(defaultPathForQuestion(item));
            }}
            sx={{ width: '100%' }}
          />
          {usesNonDefaultPath && (
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>
              This rule uses the answer&apos;s {pathLabel(path)}.
            </Typography>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Emission factor (optional)</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            The answer is multiplied by this factor to produce kg CO₂e. Without a factor the rule
            only produces an activity amount.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Emission factor year:&nbsp;<strong>{calendarYear}</strong>
          </Typography>
          <ConversionFactorPicker
            initialConversionId={open ? existingRule?.conversionId ?? null : null}
            onChange={setSelection}
            calendarYear={calendarYear}
          />
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Only calculate when conditions are met (optional)</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            When enabled, every condition below must match the respondent&apos;s answers before this rule runs.
          </Typography>
          <FormControlLabel
            control={<Switch checked={useConditions} onChange={e => setUseConditions(e.target.checked)} />}
            label='Only calculate when conditions are met'
          />
          {useConditions && (
            <Box sx={{ mt: 2 }}>
              <ConditionBuilder
                conditions={conditions}
                onChange={setConditions}
                candidateItems={groupItems}
              />
            </Box>
          )}
        </Box>

        {onSwitchToAdvanced && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Button size='small' variant='text' onClick={handleSwitchToAdvanced}>
              Switch to advanced formula
            </Button>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
              For rules that combine several answers or need calculation steps. Nothing is lost by switching.
            </Typography>
          </Box>
        )}
      </Stack>
    </DialogWrapper>
  );
};
